import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'

import { Botao, Campo, Cartao, classesEntrada, Esqueleto, TituloSecao, Vazio } from '@/componentes/ui'
import { formatarData, hojeISO } from '@/lib/datas'
import { supabase } from '@/lib/supabase'
import { cn, mensagemDeErro } from '@/lib/utilitarios'
import type { Turma } from '@/tipos/tabelas'
import { CHAVES_ADMIN, type TurmaComContagem, useProgramaAtual, useTurmasAdmin } from './comum'

/** Cadastro de turmas (seção 14). */
export function SecaoTurmas() {
  const { data: programa } = useProgramaAtual()
  const clienteQuery = useQueryClient()
  const [nome, setNome] = useState('')
  const [dataInicio, setDataInicio] = useState(hojeISO())
  const [erro, setErro] = useState<string | null>(null)

  const { data: turmas = [], isLoading } = useTurmasAdmin()

  const criar = useMutation({
    mutationFn: async () => {
      if (!programa) throw new Error('Nenhum programa ativo encontrado.')
      const { error } = await supabase.from('turmas').insert({
        programa_id: programa.id,
        nome: nome.trim(),
        data_inicio: dataInicio,
      })
      if (error) throw error
    },
    onSuccess: () => {
      setNome('')
      setErro(null)
      void clienteQuery.invalidateQueries({ queryKey: CHAVES_ADMIN.turmas })
    },
    onError: (e) => setErro(mensagemDeErro(e)),
  })

  const alternarAtiva = useMutation({
    mutationFn: async (turma: Turma) => {
      const { error } = await supabase
        .from('turmas')
        .update({ ativa: !turma.ativa })
        .eq('id', turma.id)
      if (error) throw error
    },
    onSuccess: () => void clienteQuery.invalidateQueries({ queryKey: CHAVES_ADMIN.turmas }),
  })

  const salvarEdicao = useMutation({
    mutationFn: async ({
      id,
      nome: novoNome,
      dataInicio: novaData,
    }: {
      id: string
      nome: string
      dataInicio: string
    }) => {
      const { error } = await supabase
        .from('turmas')
        .update({ nome: novoNome.trim(), data_inicio: novaData })
        .eq('id', id)
      if (error) throw error
    },
    onSuccess: () => void clienteQuery.invalidateQueries({ queryKey: CHAVES_ADMIN.turmas }),
  })

  const excluir = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from('turmas').delete().eq('id', id)
      if (error) throw error
    },
    onSuccess: () => void clienteQuery.invalidateQueries({ queryKey: CHAVES_ADMIN.turmas }),
  })

  return (
    <div className="flex flex-col gap-6">
      <section>
        <TituloSecao>Nova turma</TituloSecao>
        <Cartao className="flex flex-col gap-4">
          <Campo rotulo="Nome da turma" dica="Ex.: Turma 1 — Agosto/2026">
            <input
              type="text"
              value={nome}
              onChange={(e) => setNome(e.target.value)}
              placeholder="Turma 1"
              className={classesEntrada}
            />
          </Campo>

          <Campo
            rotulo="Data de início"
            dica="É a partir daqui que o Dia 1 é contado para quem entrar na turma."
          >
            <input
              type="date"
              value={dataInicio}
              onChange={(e) => setDataInicio(e.target.value)}
              className={classesEntrada}
            />
          </Campo>

          {erro && (
            <p role="alert" className="text-sm text-pink">
              {erro}
            </p>
          )}

          <Botao
            tamanho="bloco"
            disabled={!nome.trim() || !programa}
            carregando={criar.isPending}
            onClick={() => criar.mutate()}
          >
            Criar turma
          </Botao>
        </Cartao>
      </section>

      <section>
        <TituloSecao>Turmas</TituloSecao>

        {isLoading ? (
          <Esqueleto className="h-32" />
        ) : turmas.length === 0 ? (
          <Vazio emoji="🎓" titulo="Nenhuma turma ainda" descricao="Crie a primeira acima." />
        ) : (
          <div className="flex flex-col gap-2">
            {turmas.map((turma) => (
              <LinhaTurma
                key={turma.id}
                turma={turma}
                salvandoEdicao={salvarEdicao.isPending}
                aoSalvarEdicao={(campos) => salvarEdicao.mutate({ id: turma.id, ...campos })}
                aoAlternarAtiva={() => alternarAtiva.mutate(turma)}
                aoExcluir={() => excluir.mutate(turma.id)}
                excluindo={excluir.isPending}
              />
            ))}
          </div>
        )}
      </section>
    </div>
  )
}

function LinhaTurma({
  turma,
  salvandoEdicao,
  aoSalvarEdicao,
  aoAlternarAtiva,
  aoExcluir,
  excluindo,
}: {
  turma: TurmaComContagem
  salvandoEdicao: boolean
  aoSalvarEdicao: (campos: { nome: string; dataInicio: string }) => void
  aoAlternarAtiva: () => void
  aoExcluir: () => void
  excluindo: boolean
}) {
  const [editando, setEditando] = useState(false)
  const [nome, setNome] = useState(turma.nome)
  const [dataInicio, setDataInicio] = useState(turma.data_inicio)
  const [confirmandoExclusao, setConfirmandoExclusao] = useState(false)

  const contagem = turma.matriculas[0]?.count ?? 0
  const podeExcluir = contagem === 0

  if (editando) {
    return (
      <Cartao className="flex flex-col gap-3 p-4">
        <input
          type="text"
          value={nome}
          onChange={(e) => setNome(e.target.value)}
          className="h-12 w-full rounded-xl border-2 border-[var(--borda)] bg-[var(--fundo)] px-3 font-titulo text-[15px] font-semibold"
        />
        <input
          type="date"
          value={dataInicio}
          onChange={(e) => setDataInicio(e.target.value)}
          className="h-11 w-full rounded-xl border-2 border-[var(--borda)] bg-[var(--fundo)] px-3 text-sm"
        />
        <div className="flex gap-2">
          <Botao
            tamanho="pequeno"
            disabled={!nome.trim()}
            carregando={salvandoEdicao}
            onClick={() => {
              aoSalvarEdicao({ nome, dataInicio })
              setEditando(false)
            }}
          >
            Salvar
          </Botao>
          <Botao
            variante="fantasma"
            tamanho="pequeno"
            onClick={() => {
              setNome(turma.nome)
              setDataInicio(turma.data_inicio)
              setEditando(false)
            }}
          >
            Cancelar
          </Botao>
        </div>
      </Cartao>
    )
  }

  return (
    <Cartao className="p-4">
      <div className="flex items-center gap-3">
        <button
          type="button"
          onClick={() => setEditando(true)}
          className="min-w-0 flex-1 text-left"
        >
          <p className="font-titulo text-[15px] font-semibold">
            {turma.nome} <span aria-hidden className="text-[var(--tinta-apagada)]">✎</span>
          </p>
          <p className="text-sm text-[var(--tinta-apagada)]">
            Início em {formatarData(turma.data_inicio)} · {contagem} matriculada
            {contagem === 1 ? '' : 's'}
          </p>
        </button>

        <Botao
          variante={turma.ativa ? 'sucesso' : 'fantasma'}
          tamanho="pequeno"
          onClick={aoAlternarAtiva}
        >
          {turma.ativa ? 'Ativa' : 'Inativa'}
        </Botao>
      </div>

      {confirmandoExclusao ? (
        <div className="mt-3 flex items-center justify-between gap-2 rounded-xl bg-pink/10 p-3">
          <p className="text-xs text-pink">Excluir esta turma? Não dá para desfazer.</p>
          <div className="flex shrink-0 gap-2">
            <Botao
              variante="secundario"
              tamanho="pequeno"
              carregando={excluindo}
              onClick={aoExcluir}
            >
              Excluir
            </Botao>
            <Botao variante="fantasma" tamanho="pequeno" onClick={() => setConfirmandoExclusao(false)}>
              Cancelar
            </Botao>
          </div>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => setConfirmandoExclusao(true)}
          disabled={!podeExcluir}
          title={
            podeExcluir
              ? undefined
              : 'Mova as participantes para outra turma antes de excluir esta.'
          }
          className={cn(
            'mt-2 text-xs font-medium underline',
            podeExcluir ? 'text-[var(--tinta-apagada)]' : 'cursor-not-allowed text-[var(--borda)]',
          )}
        >
          Excluir turma
        </button>
      )}
    </Cartao>
  )
}
